import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import BackLink from "../ui/BackLink/BackLink";
import { similarKanjiGroups } from "../../consts/similarKanjiGroups";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { useSimilarKanjiStats } from "../../hooks/useSimilarKanjiStats";
import { buildRound, readingOrNull, SimilarKanjiQuestion } from "./similarKanjiQuizHelpers";
import SimilarKanjiQuizSummary from "./SimilarKanjiQuizSummary";
import styles from "./similarKanjiQuiz.module.scss";

interface QuestionResult {
    groupId: string;
    correct: boolean;
}

interface QuestionProps {
    question: SimilarKanjiQuestion;
    index: number;
    total: number;
    onAnswered: (correct: boolean) => void;
    onNext: () => void;
}

function Question({ question, index, total, onAnswered, onNext }: Readonly<QuestionProps>) {
    const { t } = useTranslation("similarKanjiPage");
    const [picked, setPicked] = useState<string | null>(null);
    const nextRef = useRef<HTMLButtonElement>(null);
    const base = `groups.${question.groupId}`;
    const memberKey = (symbol: string) => `${base}.members.${symbol}`;
    const answered = picked !== null;
    const isCorrect = picked === question.answer;
    const isKanjiPrompt = question.direction === "kanjiToMeaning";

    // The answered choices are disabled, so hand focus to Next.
    useEffect(() => {
        if (answered) nextRef.current?.focus();
    }, [answered]);

    function choose(symbol: string) {
        if (answered) return;
        setPicked(symbol);
        onAnswered(symbol === question.answer);
    }

    const kun = readingOrNull(t(`${memberKey(question.answer)}.kunyomi`));
    const choiceClass = (symbol: string) => {
        if (!answered) return styles.choice;
        if (symbol === question.answer) return `${styles.choice} ${styles.choiceCorrect}`;
        if (symbol === picked) return `${styles.choice} ${styles.choiceWrong}`;
        return `${styles.choice} ${styles.choiceDim}`;
    };
    const answerLabel = isKanjiPrompt ? t(`${memberKey(question.answer)}.meaning`) : question.answer;

    return <div className={styles.quizCard}>
        <div className={styles.quizHeader}>
            <p className={styles.quizPrompt}>
                {t(isKanjiPrompt ? "quiz.promptKanjiToMeaning" : "quiz.promptMeaningToKanji")}
            </p>
            <span className={styles.quizProgress}>{t("quiz.progress", { current: index + 1, total })}</span>
        </div>

        {isKanjiPrompt
            ? <p className={styles.glyph} lang="ja">{question.answer}</p>
            : <div className={styles.meaningPrompt}>
                <p className={styles.meaningText}>{t(`${memberKey(question.answer)}.meaning`)}</p>
                <p className={styles.readings} lang="ja">
                    {t("onyomi")}: {t(`${memberKey(question.answer)}.onyomi`)}
                    {kun && <> &nbsp;&middot;&nbsp; {t("kunyomi")}: {kun}</>}
                </p>
            </div>}

        <div className={styles.choices} data-kind={isKanjiPrompt ? "meaning" : "kanji"}>
            {question.options.map((symbol) => <button
                key={symbol}
                type="button"
                className={choiceClass(symbol)}
                disabled={answered}
                lang={isKanjiPrompt ? undefined : "ja"}
                aria-label={isKanjiPrompt ? undefined : t("quiz.choiceKanji", { symbol })}
                onClick={() => choose(symbol)}
            >
                {isKanjiPrompt ? t(`${memberKey(symbol)}.meaning`) : symbol}
            </button>)}
        </div>

        {/* Always mounted so the outcome is announced when it appears. */}
        <div role="status" aria-live="polite" className={styles.feedbackRegion}>
            {answered && <>
                <p className={isCorrect ? styles.feedbackCorrect : styles.feedbackWrong}>
                    {isCorrect ? t("quiz.correct") : t("quiz.wrong", { answer: answerLabel })}
                </p>
                {!isCorrect && <div className={styles.explain}>
                    <strong>{t("differenceLabel")}</strong>
                    <p>{t(`${base}.difference`)}</p>
                </div>}
            </>}
        </div>

        {answered && <button ref={nextRef} type="button" className={styles.primaryButton} onClick={onNext}>
            {t(index + 1 >= total ? "quiz.seeResults" : "quiz.next")}
        </button>}
    </div>;
}

export default function SimilarKanjiQuizPage() {
    const { t, i18n } = useTranslation("similarKanjiPage");
    const lp = useLocalizedPath();
    const isPt = i18n.language === "pt";
    const { recordAnswer, signedOut, syncStatus } = useSimilarKanjiStats();
    const [questions, setQuestions] = useState(() => buildRound(similarKanjiGroups));
    const [index, setIndex] = useState(0);
    const [results, setResults] = useState<QuestionResult[]>([]);
    const finished = index >= questions.length;

    function handleAnswered(correct: boolean) {
        const question = questions[index];
        setResults((previous) => [...previous, { groupId: question.groupId, correct }]);
        recordAnswer(question.groupId, correct);
    }

    function restart() {
        setQuestions(buildRound(similarKanjiGroups));
        setResults([]);
        setIndex(0);
    }

    return <div className={styles.page}>
        <Seo title={t("quiz.seoTitle")} description={t("quiz.seoDescription")} path={isPt ? "/pt/similar-kanji/quiz" : "/similar-kanji/quiz"} lang={isPt ? "pt-PT" : undefined} />
        <Header />
        <main className={styles.main}>
            <BackLink to={lp("/similar-kanji")} label={t("quizBackLink")} />
            <h1 className={styles.title}>{t("quiz.title")}</h1>
            <p className={styles.description}>{t("quiz.description")}</p>
            {finished
                ? <SimilarKanjiQuizSummary results={results} signedOut={signedOut} saved={syncStatus !== "signedOut" && syncStatus !== "loading"} onRestart={restart} />
                : <Question
                    key={`${questions[index].groupId}-${index}`}
                    question={questions[index]}
                    index={index}
                    total={questions.length}
                    onAnswered={handleAnswered}
                    onNext={() => setIndex((value) => value + 1)}
                />}
        </main>
        <Footer />
    </div>;
}
